import { type ReactNode, useState } from 'react'

import { Button, type TButtonProps } from '@/components/atoms/Button/Button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type ButtonPopoverProps = {
  children: ReactNode
  content: ReactNode
  size?: TButtonProps['size']
  variant?: TButtonProps['variant']
  disabled?: boolean
  /** Icon shown on the LEFT of the trigger label (replaces the chevron). */
  leadingIcon?: string
  /** Extra classes merged onto the trigger button. */
  triggerClassName?: string
  contentClassName?: string
  side?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const PANEL_CLASSES =
  'bg-surface-white-50 border-surface-white-300 gap-0 rounded-lg border p-6 text-base text-inherit w-auto shadow-[0px_8px_12px_0px_rgba(42,46,81,0.04)]'

export function ButtonPopover({
  children,
  content,
  size = 'sm',
  variant = 'primary',
  disabled,
  leadingIcon,
  triggerClassName,
  contentClassName,
  side = 'bottom',
  align = 'start',
  sideOffset = 8,
  open,
  onOpenChange
}: ButtonPopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpen = open ?? internalOpen

  const handleOpenChange = (nextOpen: boolean) => {
    if (open === undefined) {
      setInternalOpen(nextOpen)
    }
    onOpenChange?.(nextOpen)
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <Button
            size={size}
            variant={variant}
            disabled={disabled}
            icon={
              leadingIcon ??
              (isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down')
            }
            iconPosition={leadingIcon ? 'left' : 'right'}
            className={triggerClassName}
          >
            {children}
          </Button>
        }
      />
      <PopoverContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn(PANEL_CLASSES, contentClassName)}
      >
        {content}
      </PopoverContent>
    </Popover>
  )
}
